import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Skeleton, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Chip,
  TablePagination,
  TextField,
  InputAdornment,
  Toolbar,
  Alert
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { api } from '../../services/api';
import { formatDate } from '../../utils/formatters';

interface WalletItem {
  user_id: number;
  username: string;
  wallet: string;
  balance: number;
  asset: string;
  status: string;
}

interface WalletsResponse {
  page: number;
  maxPages: number;
  items: WalletItem[];
}

// API function to fetch wallets data
const fetchWallets = async (page: number, limit: number, search?: string): Promise<WalletsResponse> => {
  const params: any = { page, limit };
  if (search) {
    params.search = search;
  }

  const response = await api.get('/api/v1/admin-panel/wallets', {
    params,
    headers: {
      'x-hasura-admin-secret': localStorage.getItem('hasura_admin_secret') || 'admin_secret_key'
    }
  });

  return response.data.data;
};

const WalletsPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');

  // Use React Query to fetch wallets data
  const { data: walletsData, isLoading, error } = useQuery(
    ['wallets-data', page + 1, rowsPerPage, search],
    () => fetchWallets(page + 1, rowsPerPage, search || undefined),
    {
      keepPreviousData: true,
      staleTime: 10000,
    }
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'blocked':
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Кошельки пользователей
      </Typography>
      
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Toolbar sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 } }}>
          <TextField
            placeholder="Поиск по имени пользователя..."
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
            size="small"
          />
        </Toolbar>

        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            Ошибка загрузки данных: {error instanceof Error ? error.message : 'Неизвестная ошибка'}
          </Alert>
        )}

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID пользователя</TableCell>
              <TableCell>Имя пользователя</TableCell>
              <TableCell>Адрес кошелька</TableCell>
              <TableCell>Баланс</TableCell>
              <TableCell>Актив</TableCell>
              <TableCell>Статус</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              // Показываем скелетоны во время загрузки
              Array.from({ length: rowsPerPage }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton width="60px" /></TableCell>
                  <TableCell><Skeleton width="120px" /></TableCell>
                  <TableCell><Skeleton width="200px" /></TableCell>
                  <TableCell><Skeleton width="80px" /></TableCell>
                  <TableCell><Skeleton width="60px" /></TableCell>
                  <TableCell><Skeleton width="80px" /></TableCell>
                </TableRow>
              ))
            ) : walletsData?.items && walletsData.items.length > 0 ? (
              // Показываем реальные данные
              walletsData.items.map((item: WalletItem, index: number) => (
                <TableRow key={`${item.user_id}-${index}`} hover>
                  <TableCell>{item.user_id}</TableCell>
                  <TableCell>{item.username || 'Н/Д'}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    {item.wallet || 'Н/Д'}
                  </TableCell>
                  <TableCell>{item.balance || '0'}</TableCell>
                  <TableCell>{item.asset || 'Н/Д'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={item.status || 'unknown'} 
                      color={getStatusColor(item.status)} 
                      size="small" 
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {search ? 'Ничего не найдено по вашему запросу' : 'Список кошельков пуст'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={walletsData?.maxPages ? (walletsData.maxPages * rowsPerPage) : 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Строк на странице:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} из ${count !== -1 ? count : `более чем ${to}`}`
          }
        />
      </Paper>
    </Box>
  );
};

export default WalletsPage; 